/**
 * Favorites service utilities
 */

export const FAVORITES_SOURCE_KEY = 'favoritesSource';

/**
 * Store favorites in localStorage as backup
 * @param {string[]} favorites - Array of favorite item IDs
 */
export const storeFavoritesLocally = (favorites) => {
    try {
        localStorage.setItem('favorites', JSON.stringify(favorites));
    } catch (error) {
        console.warn('Failed to store favorites locally:', error);
    }
};

/**
 * Load favorites from localStorage
 * @returns {string[]} Array of favorite item IDs
 */
export const loadFavoritesLocally = () => {
    try {
        const stored = localStorage.getItem('favorites');
        return stored ? JSON.parse(stored) : [];
    } catch (error) {
        console.warn('Failed to load favorites from localStorage:', error);
        return [];
    }
};

/**
 * Check if an item is in the favorites list
 * @param {string[]} favorites - Array of favorite item IDs
 * @param {string} item - Item ID to check
 * @returns {boolean}
 */
export const isFavoriteItem = (favorites, item) => {
    return favorites.includes(item);
};

/**
 * Add an item to favorites list
 * @param {string[]} favorites - Current favorites array
 * @param {string} item - Item ID to add
 * @returns {string[]} New favorites array
 */
export const addToFavorites = (favorites, item) => {
    if (favorites.includes(item)) {
        return favorites;
    }
    return [...favorites, item];
};

/**
 * Remove an item from favorites list
 * @param {string[]} favorites - Current favorites array
 * @param {string} item - Item ID to remove
 * @returns {string[]} New favorites array
 */
export const removeFromFavorites = (favorites, item) => {
    return favorites.filter(fav => fav !== item);
};

/**
 * Toggle an item in favorites list
 * @param {string[]} favorites - Current favorites array
 * @param {string} item - Item ID to toggle
 * @returns {string[]} New favorites array
 */
export const toggleInFavorites = (favorites, item) => {
    if (favorites.includes(item)) {
        return removeFromFavorites(favorites, item);
    }
    return addToFavorites(favorites, item);
};

/**
 * Sync local favorites with server favorites
 * Finds items that exist locally but not on server
 * @param {string[]} localFavorites - Local favorites array
 * @param {string[]} serverFavorites - Server favorites array
 * @returns {object} Sync information with missing items and merged list
 */
export const syncFavorites = (localFavorites, serverFavorites) => {
    const missingOnServer = localFavorites.filter(item => !serverFavorites.includes(item));
    const mergedFavorites = [...new Set([...serverFavorites, ...localFavorites])];

    return {
        missingOnServer,
        mergedFavorites,
        needsSync: missingOnServer.length > 0
    };
};

/**
 * Clear local favorites storage
 */
export const clearLocalFavorites = () => {
    try {
        localStorage.removeItem('favorites');
    } catch (error) {
        console.warn('Failed to clear local favorites:', error);
    }
};

/**
 * Set the source of favorites in localStorage
 * @param {string} source - Source identifier
 */
export const setFavoritesSource = (source) => {
    try {
        localStorage.setItem(FAVORITES_SOURCE_KEY, source);
    } catch (e) { }
};

/**
 * Get the source of favorites from localStorage
 * @returns {string|null} Source identifier or null if not set
 */
export const getFavoritesSource = () => {
    try {
        return localStorage.getItem(FAVORITES_SOURCE_KEY);
    } catch (e) { return null; }
};

/**
 * Clear the favorites source from localStorage
 */
export const clearFavoritesSource = () => {
    try {
        localStorage.removeItem(FAVORITES_SOURCE_KEY);
    } catch (e) { }
};
